import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CalendarBoard, { ENTRY_TYPES } from "@/components/CalendarBoard";
import { Plus, FloppyDisk, X, MicrosoftOutlookLogo, Copy, ArrowsClockwise } from "@phosphor-icons/react";

const inputCls =
  "rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent";

const EMPTY = { start_date: "", end_date: "", type: "rental", status: "booked", notes: "" };

// Status vocabulary depends on the entry type: rentals are Booked/Available,
// personal & guest stays are Confirmed/Tentative. Switching type maps between them.
const STATUS_OPTIONS = {
  rental: [
    { value: "booked", label: "Booked" },
    { value: "available", label: "Available" },
  ],
  other: [
    { value: "confirmed", label: "Confirmed" },
    { value: "tentative", label: "Tentative" },
  ],
};

const statusForType = (type, status) =>
  type === "rental"
    ? status === "tentative" || status === "available" ? "available" : "booked"
    : status === "available" || status === "tentative" ? "tentative" : "confirmed";

export default function AdminCalendar() {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/calendar");
      setEntries(data.entries || []);
    } catch {
      toast.error("Could not load calendar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({ start_date: e.start_date, end_date: e.end_date, type: e.type, status: e.status, notes: e.notes || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const save = async (ev) => {
    ev.preventDefault();
    if (!form.start_date || !form.end_date) {
      toast.error("Start and end dates are required");
      return;
    }
    setSaving(true);
    try {
      let data;
      if (editingId) {
        ({ data } = await api.put(`/admin/calendar/${editingId}`, form));
        toast.success("Entry updated");
      } else {
        ({ data } = await api.post("/admin/calendar", form));
        toast.success("Entry added");
      }
      const ov = data?.overlaps || [];
      if (ov.length) {
        toast.warning(
          "Heads up — this overlaps with: " +
            ov.map((o) => `${ENTRY_TYPES[o.type]?.label || o.type} (${o.start_date} → ${o.end_date})`).join(", "),
          { duration: 8000 }
        );
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    try {
      await api.delete(`/admin/calendar/${e.id}`);
      toast.success("Entry removed");
      if (editingId === e.id) cancelEdit();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ---- Outlook / phone calendar sync link ----
  const [syncUrl, setSyncUrl] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);

  const revealSyncLink = async () => {
    setSyncBusy(true);
    try {
      const { data } = await api.get("/admin/calendar/feed-token");
      setSyncUrl(`${API}/calendar/feed.ics?key=${data.token}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSyncBusy(false);
    }
  };

  const copySyncLink = async () => {
    try {
      await navigator.clipboard.writeText(syncUrl);
      toast.success("Sync link copied — paste it into Outlook or your phone's calendar");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const regenSyncLink = async () => {
    setSyncBusy(true);
    try {
      const { data } = await api.post("/admin/calendar/feed-token/regenerate");
      setSyncUrl(`${API}/calendar/feed.ics?key=${data.token}`);
      toast.success("New link created — the old one no longer works. Re-share it with your wife and manager.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSyncBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-calendar">
      <div>
        <span className="cms-label text-accent">Owner Tools</span>
        <h2 className="font-display text-4xl tracking-tight text-foreground mt-2">Calendar</h2>
        <p className="text-muted-foreground mt-3 text-sm max-w-lg">
          Block out personal use, guest stays, and rentals. Check-in is 4:00 PM on the start date
          and check-out is 10:00 AM on the end date. Managers see this calendar read-only when they
          log in with their code.
        </p>
      </div>

      <form onSubmit={save} className="border border-border p-5 bg-secondary/40 space-y-4" data-testid="calendar-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <label className="subhead text-foreground/50 text-xs">Start date (check-in 4:00 PM)</label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className={inputCls}
              data-testid="cal-start-input"
            />
          </div>
          <div className="space-y-2">
            <label className="subhead text-foreground/50 text-xs">End date (check-out 10:00 AM)</label>
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className={inputCls}
              data-testid="cal-end-input"
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <label className="subhead text-foreground/50 text-xs">Type</label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v, status: statusForType(v, f.status) }))}>
              <SelectTrigger data-testid="cal-type-select" className="rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ENTRY_TYPES).map(([k, t]) => (
                  <SelectItem key={k} value={k} data-testid={`cal-type-${k}`}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: t.color }} />
                      {t.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="subhead text-foreground/50 text-xs">Status</label>
            <Select key={form.type} value={form.status} onValueChange={(v) => v && setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="cal-status-select" className="rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(form.type === "rental" ? STATUS_OPTIONS.rental : STATUS_OPTIONS.other).map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`cal-status-${s.value}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <label className="subhead text-foreground/50 text-xs">Notes (renter name, details…)</label>
          <Input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. Smith family rental — paid via VRBO"
            className={inputCls}
            data-testid="cal-notes-input"
          />
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving} data-testid="cal-save-btn" className="inline-flex items-center gap-2 bg-foreground text-primary-foreground pl-5 pr-6 py-3 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300 disabled:opacity-60">
            {editingId ? <FloppyDisk size={15} weight="bold" /> : <Plus size={15} weight="bold" />}
            {saving ? "Saving…" : editingId ? "Save changes" : "Add entry"}
          </button>
          {editingId && (
            <button type="button" onClick={cancelEdit} data-testid="cal-cancel-edit-btn" className="inline-flex items-center gap-2 border border-border px-5 py-3 rounded-full text-sm hover:bg-secondary transition-colors">
              <X size={14} /> Cancel
            </button>
          )}
        </div>
      </form>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <CalendarBoard entries={entries} canEdit onEdit={startEdit} onDelete={remove} />
      )}

      <div className="border border-border p-5 bg-secondary/40 space-y-3" data-testid="calendar-sync">
        <div className="flex items-center gap-2">
          <MicrosoftOutlookLogo size={16} className="text-accent" />
          <p className="cms-label text-foreground/60">Sync to Outlook & Phones</p>
        </div>
        <p className="text-xs text-muted-foreground max-w-lg">
          Share this private link with your wife or manager — they subscribe once, and every change
          you make here shows up in their calendar automatically. Entries stay editable only here.
        </p>
        <div className="text-xs text-muted-foreground space-y-1.5 max-w-lg" data-testid="sync-instructions">
          <p><span className="font-semibold text-foreground/80">PC (Outlook):</span> Calendar → Add calendar → Subscribe from web → paste the link → Import.</p>
          <p><span className="font-semibold text-foreground/80">Mac (Apple Calendar):</span> File → New Calendar Subscription… → paste the link → Subscribe.</p>
          <p><span className="font-semibold text-foreground/80">iPhone:</span> Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar → paste the link. (Or simply open this page on the iPhone and tap the link.)</p>
        </div>
        {syncUrl ? (
          <div className="space-y-3">
            <p className="text-xs break-all bg-background border border-border p-3 text-foreground/80" data-testid="sync-url-text">
              {syncUrl}
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={copySyncLink} data-testid="sync-copy-btn" className="inline-flex items-center gap-2 bg-foreground text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300">
                <Copy size={14} weight="bold" /> Copy link
              </button>
              <button onClick={regenSyncLink} disabled={syncBusy} data-testid="sync-regen-btn" className="inline-flex items-center gap-2 border border-border px-5 py-2.5 rounded-full text-sm hover:bg-secondary transition-colors disabled:opacity-60">
                <ArrowsClockwise size={14} /> Regenerate link
              </button>
            </div>
          </div>
        ) : (
          <button onClick={revealSyncLink} disabled={syncBusy} data-testid="sync-link-btn" className="inline-flex items-center gap-2 bg-foreground text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300 disabled:opacity-60">
            <MicrosoftOutlookLogo size={15} weight="bold" /> {syncBusy ? "Loading…" : "Show sync link"}
          </button>
        )}
      </div>
    </div>
  );
}
